/**
 * VÉRAULT Portal — Records (Wave 35 · Phase 1 · PI-3)
 *
 * Firm-level Records area. Lists every client + every experience with
 * their permanent codes (VLT-C-…, VLT-E-…) and exposes a universal
 * search across record types.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Archive, Search, Loader2, Users, Plane, KeyRound, BookOpen } from "lucide-react";
import {
  Surface,
  PortalButton,
  StatusTag,
  PageHeader,
  EditorialRule,
  AIWhisper,
  MetricTile,
  PortalBreadcrumb,
  api,
  motion as portalMotion,
} from "../../index";

const { fadeInUp, stagger } = portalMotion;

const SECTIONS = [
  { id: "all", label: "All" },
  { id: "clients", label: "Clients" },
  { id: "experiences", label: "Experiences" },
  { id: "portal_access", label: "Portal Access" },
];

function FmtDate({ value }) {
  if (!value) return <span className="text-[hsl(var(--foreground-muted))]">—</span>;
  try {
    return <span>{new Date(value).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" })}</span>;
  } catch {
    return <span>{String(value)}</span>;
  }
}

function FmtDateTime({ value }) {
  if (!value) return <span className="text-[hsl(var(--foreground-muted))]">never</span>;
  try {
    return <span>{new Date(value).toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" })}</span>;
  } catch {
    return <span>{String(value)}</span>;
  }
}

function ClientRow({ row }) {
  return (
    <Link
      to={`/portal/advisor/clients/${row.id}`}
      className="block rounded border border-[hsl(var(--surface-hairline))] bg-[hsl(var(--background-paper))] hover:border-[hsl(var(--accent-gold))]/40 transition-colors p-3"
      data-testid={`records-client-${row.client_code || row.id}`}
    >
      <div className="flex items-start gap-3 flex-wrap">
        <span className="inline-flex items-center justify-center size-8 rounded-full bg-[hsl(var(--background-stone))] text-[hsl(var(--foreground-muted))]">
          <Users className="size-3.5" strokeWidth={1.5} />
        </span>
        <div className="flex-1 min-w-0">
          <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--accent-gold))]">
            {row.client_code || "—"}
          </p>
          <p className="font-serif text-[15px] mt-0.5">{row.full_name}</p>
          <p className="font-mono text-[11px] text-[hsl(var(--foreground-muted))] mt-0.5">
            {row.email || "no email"} {row.phone ? `· ${row.phone}` : ""}
          </p>
        </div>
        <StatusTag tone={row.status === "active" ? "success" : "muted"}>{row.status || "lead"}</StatusTag>
      </div>
    </Link>
  );
}

function ExperienceRow({ row }) {
  return (
    <Link
      to={`/portal/advisor/journeys/${row.id}`}
      className="block rounded border border-[hsl(var(--surface-hairline))] bg-[hsl(var(--background-paper))] hover:border-[hsl(var(--accent-gold))]/40 transition-colors p-3"
      data-testid={`records-experience-${row.experience_code || row.id}`}
    >
      <div className="flex items-start gap-3 flex-wrap">
        <span className="inline-flex items-center justify-center size-8 rounded-full bg-[hsl(var(--background-stone))] text-[hsl(var(--foreground-muted))]">
          <Plane className="size-3.5" strokeWidth={1.5} />
        </span>
        <div className="flex-1 min-w-0">
          <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--accent-gold))]">
            {row.experience_code || "—"}
          </p>
          <p className="font-serif text-[15px] mt-0.5">
            {row.trip_title || row.internal_trip_name || row.destinations || "Untitled experience"}
          </p>
          <p className="font-mono text-[11px] text-[hsl(var(--foreground-muted))] mt-0.5">
            {row.client_name || "—"}
            {row.trip_start_date ? <> · <FmtDate value={row.trip_start_date} /> → <FmtDate value={row.trip_end_date} /></> : null}
          </p>
        </div>
        <div className="flex items-center gap-1.5">
          {row.framework === "design_studio" && <StatusTag tone="gold">Design Studio</StatusTag>}
          {row.lifecycle_status && <StatusTag tone="neutral">{row.lifecycle_status.replace(/_/g, " ")}</StatusTag>}
        </div>
      </div>
    </Link>
  );
}

function PortalAccessRow({ row }) {
  return (
    <Link
      to={`/portal/advisor/clients/${row.client_id}?tab=portal_access`}
      className="block rounded border border-[hsl(var(--surface-hairline))] bg-[hsl(var(--background-paper))] hover:border-[hsl(var(--accent-gold))]/40 transition-colors p-3"
      data-testid={`records-portal-${row.client_id}`}
    >
      <div className="flex items-start gap-3 flex-wrap">
        <span className="inline-flex items-center justify-center size-8 rounded-full bg-[hsl(var(--background-stone))] text-[hsl(var(--foreground-muted))]">
          <KeyRound className="size-3.5" strokeWidth={1.5} />
        </span>
        <div className="flex-1 min-w-0">
          <p className="font-serif text-[15px]">{row.name}</p>
          <p className="font-mono text-[11px] text-[hsl(var(--foreground-muted))] mt-0.5">
            {row.email} · last login <FmtDateTime value={row.last_login_at} />
          </p>
        </div>
        <div className="flex items-center gap-1.5">
          <StatusTag tone={row.is_active ? "success" : "muted"}>{row.is_active ? "Active" : "Disabled"}</StatusTag>
          <StatusTag tone="neutral">{(row.access_level || "primary_client").replace(/_/g, " ")}</StatusTag>
        </div>
      </div>
    </Link>
  );
}

function SearchHit({ row }) {
  const Icon = row.kind === "client" ? Users : row.kind === "experience" ? Plane : BookOpen;
  return (
    <Link
      to={row.href || "#"}
      className="block rounded border border-[hsl(var(--surface-hairline))] bg-[hsl(var(--background-paper))] hover:border-[hsl(var(--accent-gold))]/40 transition-colors p-3"
      data-testid={`records-search-hit-${row.kind}-${row.id}`}
    >
      <div className="flex items-start gap-3">
        <span className="inline-flex items-center justify-center size-8 rounded-full bg-[hsl(var(--background-stone))] text-[hsl(var(--foreground-muted))]">
          <Icon className="size-3.5" strokeWidth={1.5} />
        </span>
        <div className="flex-1 min-w-0">
          <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--accent-gold))]">
            {row.kind}{row.code ? ` · ${row.code}` : ""}
          </p>
          <p className="font-serif text-[15px] mt-0.5">{row.title}</p>
          {row.subtitle && <p className="font-mono text-[11px] text-[hsl(var(--foreground-muted))] mt-0.5">{row.subtitle}</p>}
        </div>
      </div>
    </Link>
  );
}

export default function Records() {
  const [section, setSection] = useState("all");
  const [q, setQ] = useState("");
  const [summary, setSummary] = useState(null);
  const [clients, setClients] = useState([]);
  const [experiences, setExperiences] = useState([]);
  const [portalRows, setPortalRows] = useState([]);
  const [searchHits, setSearchHits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);

  // Load summary + sections
  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const [s, c, e, pa] = await Promise.all([
          api.get("/admin/records/summary").then((r) => r.data).catch(() => null),
          api.get("/admin/records/clients?limit=200").then((r) => r.data?.rows || []).catch(() => []),
          api.get("/admin/records/experiences?limit=200").then((r) => r.data?.rows || []).catch(() => []),
          api.get("/admin/clients").then((r) => r.data?.rows || []).catch(() => []),
        ]);
        if (!alive) return;
        setSummary(s);
        setClients(c);
        setExperiences(e);
        setPortalRows(pa);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  // Debounced universal search
  useEffect(() => {
    if (!q.trim()) {
      setSearchHits([]);
      return;
    }
    setSearching(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/admin/records/search?q=${encodeURIComponent(q.trim())}&limit=30`);
        setSearchHits(data?.rows || []);
      } catch {
        setSearchHits([]);
      } finally {
        setSearching(false);
      }
    }, 220);
    return () => clearTimeout(t);
  }, [q]);

  const isSearching = q.trim().length > 0;

  const filteredClients = useMemo(() => {
    if (!q.trim()) return clients;
    const rx = q.toLowerCase();
    return clients.filter((c) => `${c.full_name || ""} ${c.email || ""} ${c.client_code || ""}`.toLowerCase().includes(rx));
  }, [clients, q]);

  const filteredExperiences = useMemo(() => {
    if (!q.trim()) return experiences;
    const rx = q.toLowerCase();
    return experiences.filter((t) =>
      `${t.trip_title || ""} ${t.internal_trip_name || ""} ${t.destinations || ""} ${t.experience_code || ""} ${t.client_name || ""}`.toLowerCase().includes(rx)
    );
  }, [experiences, q]);

  return (
    <motion.div variants={stagger} initial="initial" animate="animate" className="space-y-6" data-testid="records-page">
      <PortalBreadcrumb
        parent={{ to: "/portal/advisor", label: "Atelier" }}
        trail={[{ label: "Records" }]}
      />
      <PageHeader
        eyebrow="VÉRAULT · Internal"
        title="Records"
        lede="Every client, every experience, every code &mdash; one quiet ledger."
      />

      <motion.div variants={fadeInUp}>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <MetricTile label="Clients" value={summary?.clients ?? 0} format="number" icon={Users} />
          <MetricTile label="Experiences" value={summary?.experiences ?? 0} format="number" icon={Plane} />
          <MetricTile label="Portal accounts" value={summary?.portal_accounts ?? 0} format="number" icon={KeyRound} />
          <MetricTile label="Agreements" value={summary?.agreements ?? 0} format="number" icon={BookOpen} />
        </div>
      </motion.div>

      <motion.div variants={fadeInUp}>
        <Surface variant="paper" padded className="!p-4">
          <div className="flex items-center gap-3 flex-wrap">
            <Search className="size-4 text-[hsl(var(--foreground-muted))] shrink-0" />
            <input
              type="search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search across clients, experiences, agreements, forms, and codes (e.g. VLT-E-26-PO-001)…"
              className="flex-1 min-w-0 bg-transparent border-none outline-none font-mono text-[13px] placeholder:text-[hsl(var(--foreground-muted))]"
              data-testid="records-search-input"
            />
            {searching && <Loader2 className="size-4 animate-spin text-[hsl(var(--foreground-muted))]" />}
          </div>
        </Surface>
      </motion.div>

      {/* Section tabs */}
      <motion.div variants={fadeInUp}>
        <nav className="flex flex-wrap gap-1 sm:gap-2" data-testid="records-tabs">
          {SECTIONS.map((s) => (
            <button
              key={s.id}
              onClick={() => setSection(s.id)}
              data-testid={`records-tab-${s.id}`}
              className={`group relative px-3 py-2 font-body text-[13px] tracking-tight transition-colors duration-200 ${
                section === s.id ? "text-[hsl(var(--foreground))]" : "text-[hsl(var(--foreground-muted))] hover:text-[hsl(var(--foreground))]"
              }`}
            >
              <span className={section === s.id ? "font-serif" : ""}>{s.label}</span>
              <span
                className={`absolute -bottom-px left-3 right-3 h-px transition-colors duration-300 ${
                  section === s.id ? "bg-[hsl(var(--accent-gold))]" : "bg-transparent"
                }`}
                aria-hidden
              />
            </button>
          ))}
        </nav>
      </motion.div>

      {/* Universal search results when query active */}
      {isSearching && searchHits.length > 0 && section === "all" && (
        <motion.div variants={fadeInUp} className="space-y-2">
          <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))]">
            Universal search · {searchHits.length} {searchHits.length === 1 ? "match" : "matches"}
          </p>
          {searchHits.map((h) => <SearchHit key={`${h.kind}-${h.id}`} row={h} />)}
        </motion.div>
      )}

      {loading ? (
        <Surface variant="paper" padded>
          <p className="font-accent italic text-[13px] text-[hsl(var(--foreground-muted))] flex items-center gap-2">
            <Loader2 className="size-3.5 animate-spin" /> Loading records…
          </p>
        </Surface>
      ) : (
        <>
          {(section === "all" || section === "clients") && (
            <motion.div variants={fadeInUp} className="space-y-2">
              <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))]">
                Clients · {filteredClients.length}
              </p>
              {filteredClients.length === 0 ? (
                <Surface variant="paper" padded>
                  <p className="font-accent italic text-[13px] text-[hsl(var(--foreground-muted))]">No clients match.</p>
                </Surface>
              ) : (
                filteredClients.map((c) => <ClientRow key={c.id} row={c} />)
              )}
            </motion.div>
          )}

          {(section === "all" || section === "experiences") && (
            <motion.div variants={fadeInUp} className="space-y-2">
              <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))]">
                Experiences · {filteredExperiences.length}
              </p>
              {filteredExperiences.length === 0 ? (
                <Surface variant="paper" padded>
                  <p className="font-accent italic text-[13px] text-[hsl(var(--foreground-muted))]">No experiences match.</p>
                </Surface>
              ) : (
                filteredExperiences.map((t) => <ExperienceRow key={t.id} row={t} />)
              )}
            </motion.div>
          )}

          {(section === "all" || section === "portal_access") && (
            <motion.div variants={fadeInUp} className="space-y-2">
              <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))]">
                Portal accounts · {portalRows.length}
              </p>
              {portalRows.length === 0 ? (
                <Surface variant="paper" padded>
                  <p className="font-accent italic text-[13px] text-[hsl(var(--foreground-muted))]">No portal accounts yet.</p>
                </Surface>
              ) : (
                portalRows.map((p) => <PortalAccessRow key={p.user_id} row={p} />)
              )}
            </motion.div>
          )}
        </>
      )}

      <EditorialRule />
      <AIWhisper label="A quiet note">
        Client Codes (<span className="font-mono">VLT-C-260001</span>) and Experience Codes (<span className="font-mono">VLT-E-26-PT-001</span>) are visible record refs.
        They are never used as passwords.
      </AIWhisper>
    </motion.div>
  );
}
